import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './endpoint-op-list';
import './endpoint-builder';
import './endpoint-op-editor';
import './integration-values-lock';
import '@vitesse/base-ui/elements/vitesse-gated';
import { button, baseButtonStyles } from '@vitesse/base-ui/components/base-buttons';
import { t, formatNumber, LocaleController } from '@vitesse/base-ui/i18n';
import { getSession } from '@vitesse/base-ui';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import { mayReadValues } from './integration-access';
import { parseSpec } from './endpoint-method';
import { statusClearsApplyError } from './apply-error';
import type { EndpointOp, EndpointParam, EndpointResponse } from './endpoint-builder';
import { EndpointResponseModel } from './endpoint-response-model';
import type { BackendClient } from '../../bridge/backend-client';
import type { Integration, IntegrationOp } from '../../../core/domain/integration';
import { showResultError } from '../../errors';
import type { StreamFrame } from '../../../core/bridge/types';
import type { TypedValue } from '../../../core/bridge/connect-client';

/** The follow window the value stream seeds/holds, matching the grid's. */
const WINDOW_MS = 30_000;

/** Path placeholders in a REST URL are the op's parameters: `GET /resource/{i}/field/{field}`
 *  → [{i}, {field}]. Query placeholders (`?x={x}`) are captured the same way and tagged
 *  `query`. The backend spec IS the ground truth (the driver refuses to observe an op with
 *  unresolved placeholders), so the URL is the authoritative param source — no separate
 *  schema needed. Deduped, order-preserving. */
function paramsFromUrl(url: string): EndpointParam[] {
  const seen = new Set<string>();
  const out: EndpointParam[] = [];
  const q = url.indexOf('?');
  for (const m of url.matchAll(/\{([^}]+)\}/g)) {
    const name = m[1];
    if (seen.has(name)) continue;
    seen.add(name);
    const inQuery = q >= 0 && (m.index ?? 0) > q;
    out.push({ name, in: inQuery ? 'query' : 'path', type: 'string', required: !inQuery });
  }
  return out;
}

/*
 * <vitesse-endpoint-viewport> — the ENDPOINT-kind integration viewport, Postman-
 * shaped: an operations list (left) + a request-builder / response pane (right).
 * REST data is not PLC symbol data, so this is deliberately NOT the SymbolGrid —
 * an op is something you INVOKE (method + URL + params), not a value you poll.
 *
 *   [ op-list 360px ]  gap  [ builder flex:1 ]
 *
 * The op-list is the selector over the discovered schema; selecting an op loads it
 * into the builder. The builder (params + addressable response leaves) is the
 * Postman heart. Live values + sparklines on the op rows + the builder's live
 * response are the value-plane pass on top of this shape.
 */
@customElement('vitesse-endpoint-viewport')
export class EndpointViewport extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() integrationId = '';
  /** The roster's view of this integration — the descriptor we re-apply when an
   *  operation is added/removed or the spec is rediscovered. (The op list learns
   *  of the resulting catalog change from its own structure stream.) */
  @property({ attribute: false }) integration?: Integration;

  @state() private op: EndpointOp | null = null;
  @state() private editorOpen = false;
  @state() private applying: '' | 'ops' | 'rediscover' = '';
  @state() private applyError = '';
  /** The last connection status seen, so a RECOVERY can be told from a screen
   *  that was already connected. Plain field, not @state: it feeds the decision
   *  in updated() and must not itself schedule a render. */
  private lastStatus?: Integration['status'];
  @state() private params: EndpointParam[] = [];
  @state() private invoking = false;
  /** Outcome of the last write — including a permission denial, which is the
   *  server's authoritative answer and already phrased for the user. */
  @state() private writeResult: { ok: boolean; text: string } | null = null;
  /** The live-response store: bounded, updated in place, stable field order —
   *  see endpoint-response-model.ts. Owned here; the op-list gets a read-only
   *  view of its values. */
  private readonly model = new EndpointResponseModel();
  /** Mirrors model.version into Lit's change detection: bumps ONLY when the
   *  current op's fields changed, so a frame for some other op (retained as
   *  cushion) costs no render at all. */
  @state() private valuesVersion = 0;
  /** The integration TARGET the selected op reads — the op node's path. A field
   *  leaf resolves to its owning op. */
  @state() private target = '';
  /** This view's window-follow tag: every tagged page fetch re-scopes the value
   *  stream to the window AND drives the edge observe-lease for its leaves. */
  private readonly clientSession = `endpoint-${Math.random().toString(36).slice(2)}`;
  private valuePort: MessagePort | null = null;

  constructor() {
    super();
    new LocaleController(this);
  }

  // baseButtonStyles FIRST, and it is not optional: this element has a shadow
  // root, `button()` emits only class names, and the global base-button sheet
  // cannot cross that boundary — so without it the toolbar's Operations and
  // Rediscover render as bare white browser buttons, misaligned against the
  // light-DOM pill tabs above them, with nothing logged to explain it.
  static styles = [
    baseButtonStyles,
    css`
    :host {
      display: flex;
      flex-direction: column;
      gap: 10px;
      box-sizing: border-box;
      min-height: 440px;
      height: 100%;
    }
    .bar {
      flex: none;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .bar .count {
      font-size: 12px;
      color: var(--text-secondary);
    }
    .bar .err {
      font-size: 12px;
      color: var(--red, #d64541);
    }
    .panes {
      flex: 1;
      min-height: 0;
      display: flex;
      gap: 14px;
    }
    vitesse-endpoint-builder {
      flex: 1;
      min-width: 0;
    }
  `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('resize', this.fit);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.fit);
    // Drop the lease with the view — an unobserved integration leaf stops being
    // produced, which is the whole point of observe-while-watched.
    this.valuePort?.postMessage({ kind: 'close' });
    this.valuePort?.close();
    this.valuePort = null;
  }
  protected firstUpdated(): void {
    this.fit();
    this.openValueStream();
    this.restoreSelection();
  }
  protected updated(): void {
    this.fit();
    // Retire a stale apply error the moment the integration comes back. The
    // retry loop reconnects on its own, so without this the failure prose stays
    // on screen over a working integration until the next click — see
    // apply-error.ts for why this keys off the TRANSITION and not the state.
    const status = this.integration?.status;
    if (status !== this.lastStatus) {
      const recovered = statusClearsApplyError(this.lastStatus, status);
      this.lastStatus = status;
      // Clearing @state re-enters updated() once; the status now matches
      // lastStatus, so that pass is a no-op and the cycle terminates.
      if (recovered && this.applyError) this.applyError = '';
    }
  }

  /** Re-apply the integration's descriptor (secrets omitted — the service
   *  merges the stored ones) with `targets` as the hand-added operations. This
   *  reconnects the driver, which re-runs spec discovery: so the same call is
   *  both "save my operations" and "rediscover". The op list's structure stream
   *  pushes the new page the moment the indexer has ingested the result. */
  private async reapply(targets: IntegrationOp[], why: 'ops' | 'rediscover'): Promise<void> {
    const i = this.integration;
    if (!this.backend || !i || this.applying) return;
    this.applying = why;
    this.applyError = '';
    try {
      const res = await this.backend.api.applyIntegration(this.gatewayId, {
        integrationId: i.id,
        type: i.type,
        displayName: i.displayName,
        description: i.description,
        fields: i.fields,
        secrets: {},
        targets,
      });
      if (!res.ok) this.applyError = res.error;
    } catch (err) {
      this.applyError = err instanceof Error ? err.message : String(err);
      showResultError(err, t('integration.opsManage'));
    } finally {
      this.applying = '';
    }
  }
  // Bound the two-pane to the viewport so the op-list virtualizer + the builder
  // have a real height to scroll within (the detail-element tag isn't height-bounded).
  private fit = (): void => {
    const top = this.getBoundingClientRect().top;
    this.style.height = `${Math.max(440, Math.round(window.innerHeight - top - 24))}px`;
  };

  private onOpSelected(e: CustomEvent<{ path: string; detail: string; kind: string; writable: boolean }>): void {
    this.selectOp(e.detail.path, e.detail.detail, true);
  }

  /** Seed from the op-list's first row — only if nothing was restored, so a
   *  remembered selection always wins over the seed. */
  private onFirstRow(e: CustomEvent<{ path: string; detail: string }>): void {
    if (this.op) return;
    this.selectOp(e.detail.path, e.detail.detail, false);
  }

  private storeKey(): string {
    return `vitesse:intg:${this.integrationId}:op`;
  }

  /** Restore the op this integration was last showing. We persist the spec next to
   *  the path so the builder renders immediately without hunting the row down in a
   *  10k-op paged list — consistent with load-only-what's-needed. */
  private restoreSelection(): void {
    try {
      const raw = localStorage.getItem(this.storeKey());
      if (!raw) return;
      const { path, detail } = JSON.parse(raw) as { path?: string; detail?: string };
      if (path) this.selectOp(path, detail ?? '', false);
    } catch {
      /* a corrupt/absent entry just means "no memory" */
    }
  }

  private selectOp(path: string, detail: string, persist: boolean): void {
    const d = { path, detail };
    // A response leaf (`live_co2/readings/zone0/field0`) is not itself invocable —
    // it's produced BY an op. Selecting one loads its OWNING op (the first path
    // segment, which is the op node / Read target) so the request that produces
    // the field is what you see and can invoke. Leaves carry their op's spec in
    // `detail`, so method/url resolve identically either way.
    const target = d.path.split('/')[0];
    const { method, url } = parseSpec(d.detail);
    // A side-effecting op (its Invoke is disabled in the browser — writes go via
    // models/SCXML): any non-read method.
    const write = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    this.target = target; // the response pane projects live values under this op
    this.model.setTarget(target); // its retained fields (if any) show at once
    this.valuesVersion = this.model.version;
    // The op-list leases only OPERATIONS (it filters to /kind=endpoint), so this
    // op's response leaves are not observed yet — scope the lease onto them so the
    // response streams in on selection rather than only after Invoke.
    void this.scopeToTarget();
    this.params = paramsFromUrl(url);
    this.op = { name: target, method, url, write };
    if (persist) {
      try {
        localStorage.setItem(this.storeKey(), JSON.stringify({ path: d.path, detail: d.detail }));
      } catch {
        /* storage full/blocked — remembering the selection is best-effort */
      }
    }
  }

  /* Invoke = OBSERVE this op now. An endpoint driver exposes Observe (not a row
   * Read), and an integration value is produced ONLY while observed — so
   * "invoking" a read op means scoping the view's observe-lease onto its leaves.
   * We issue a tagged page fetch filtered to the op: the api-gateway re-scopes the
   * value stream server-side and extends the edge lease, and the response fields
   * then stream in live. Writes are never invoked from the browser (models/SCXML
   * own that) — the builder already disables the button for them. */
  private onInvoke = async (e?: CustomEvent<{ body?: string }>): Promise<void> => {
    if (!this.backend || !this.target || this.invoking) return;
    this.invoking = true;
    try {
      if (this.op?.write) {
        await this.sendWrite(e?.detail?.body ?? '');
        return;
      }
      await this.scopeToTarget();
    } finally {
      this.invoking = false;
    }
  };

  /* Send a write. The builder's Send is gated on Integrations.Write BEFORE the
   * click (docs/gated-ui.md: the UI predicts, the backend decides), so a caller
   * without the grant sees a lock and never reaches here. This comment used to
   * say "we just send and report the answer" — the one surface on the page that
   * argued for the behaviour the gating doctrine forbids. The api-gateway is
   * still the authority: a refusal that does arrive (a stale session, a race
   * with a revocation) carries a message written for the user and is surfaced
   * verbatim rather than reworded here. */
  private async sendWrite(body: string): Promise<void> {
    try {
      const { affected } = await this.backend!.api.integrationWrite(this.gatewayId, this.integrationId, this.target, {
        body,
      });
      this.writeResult = { ok: true, text: `Sent — ${affected} affected` };
    } catch (err) {
      this.writeResult = { ok: false, text: err instanceof Error ? err.message : String(err) };
      // The pane shows the outcome in-context; the shared toast carries the full
      // denial suite (every refusal + remedy) — the platform-standard surface.
      showResultError(err, 'Write refused');
    }
  }

  /** Tagged fetch scoped to the selected op — a plain term matches the op node AND
   *  its flattened leaves, so the api-gateway extends the observe-lease over the
   *  whole response. Bounded to one op's leaves: each leased path is a real
   *  external subscribe/poll, so we never lease the whole catalog. */
  private async scopeToTarget(): Promise<void> {
    if (!this.backend || !this.target) return;
    // No integration:read → no lease. The response would never arrive (the
    // value stream below is refused), and the tagged fetch would still extend
    // an observe-lease on the customer's system. See integration-access.ts.
    if (!mayReadValues(getSession())) return;
    try {
      await this.backend.api.fetchIntegrationPage(this.gatewayId, this.integrationId, {
        fts: this.target,
        pageSize: 500,
        clientSession: this.clientSession,
      });
    } catch {
      /* best-effort — values are not load-bearing for the op list */
    }
  }

  /** The response pane is LIVE, not a captured payload: the op's flattened leaves
   *  are addressable values, so we project the current window-follow values under
   *  `<op>/` into the viewer. Empty until the lease produces them. */
  private buildResponse(): EndpointResponse | null {
    if (!this.op || !this.target) return null;
    // A write has no live response — it has an OUTCOME (sent, or denied).
    if (this.op.write) {
      if (!this.writeResult) return null;
      return {
        status: this.writeResult.ok ? 'Sent' : 'Denied',
        ok: this.writeResult.ok,
        fields: [{ key: this.writeResult.ok ? 'result' : 'error', kind: 'string', value: this.writeResult.text }],
      };
    }
    // Stable order from the model — a field keeps the position it was first
    // seen at, so a late leaf never shoves the rows under the reader.
    const fields = this.model.fields();
    if (fields.length === 0) return null;
    return { status: 'live', ok: true, timing: `${fields.length} live fields`, fields };
  }

  /** The view's ONE value stream (window-follow). Frames carry every phase —
   *  entering-leaf snapshot, seed, live tail — routed identically. */
  private openValueStream(): void {
    void (async () => {
      if (!this.backend || !this.integrationId) return;
      if (!mayReadValues(getSession())) return; // browse without read: paths, no values
      try {
        const port = await this.backend.api.subscribeIntegrationWindowValues(
          this.gatewayId,
          this.integrationId,
          this.clientSession,
          WINDOW_MS,
        );
        if (!port) return;
        port.onmessage = (e: MessageEvent<StreamFrame<TypedValue>>) => {
          const frame = e.data;
          if (frame.kind === 'error' || !('items' in frame)) return;
          // In place — no per-frame copy. Re-render only when the CURRENT op's
          // fields actually changed; other ops' values are retained silently.
          if (this.model.apply(frame.items)) this.valuesVersion = this.model.version;
        };
        port.start();
        this.valuePort = port;
      } catch {
        /* values are best-effort; the op list still renders */
      }
    })();
  }

  protected render() {
    const ops = this.integration?.targets ?? [];
    const count = this.integration?.schemaCount ?? 0;
    // Operations + Rediscover are both one Apply, so they share one lock — on
    // the button that OPENS the operations dialog, not on the dialog (a form you
    // cannot submit is a worse refusal than no form). The values chip explains
    // an empty response pane to a caller who may browse ops but not read them.
    return html`
      <div class="bar">
        <vitesse-gated .rpc=${Integrations.method.apply} inline>
          ${button({
            label: t('integration.opsManage'),
            icon: 'list-plus',
            size: 'sm',
            disabled: !this.integration || !this.backend,
            onClick: () => (this.editorOpen = true),
          })}
          ${button({
            label: this.applying === 'rediscover' ? t('integration.opsRediscovering') : t('integration.opsRediscover'),
            icon: 'refresh-cw',
            size: 'sm',
            disabled: !this.integration || !this.backend || this.applying !== '',
            onClick: () => void this.reapply(ops, 'rediscover'),
          })}
        </vitesse-gated>
        <vitesse-integration-values-lock></vitesse-integration-values-lock>
        ${count > 0 ? html`<span class="count">${t('integration.opsCount', { n: formatNumber(count) })}</span>` : ''}
        ${this.applyError && !this.editorOpen ? html`<span class="err">${this.applyError}</span>` : ''}
      </div>
      <div class="panes">
        <vitesse-endpoint-op-list
          .backend=${this.backend}
          .gatewayId=${this.gatewayId}
          .integrationId=${this.integrationId}
          .clientSession=${this.clientSession}
          .values=${this.model.values}
          .valuesVersion=${this.valuesVersion}
          @op-selected=${(e: CustomEvent) => this.onOpSelected(e as CustomEvent<{ path: string; detail: string; kind: string; writable: boolean }>)}
          @first-row=${(e: CustomEvent) => this.onFirstRow(e as CustomEvent<{ path: string; detail: string }>)}
        ></vitesse-endpoint-op-list>
        <vitesse-endpoint-builder
          .op=${this.op}
          .params=${this.params}
          .response=${this.buildResponse()}
          ?invoking=${this.invoking}
          @invoke=${(e: CustomEvent) => this.onInvoke(e as CustomEvent<{ body?: string }>)}
        ></vitesse-endpoint-builder>
      </div>
      <vitesse-endpoint-op-editor
        .open=${this.editorOpen}
        .ops=${ops}
        ?busy=${this.applying === 'ops'}
        .error=${this.applyError}
        @ops-change=${(e: CustomEvent<IntegrationOp[]>) => void this.reapply(e.detail, 'ops')}
        @dialog-close=${() => (this.editorOpen = false)}
      ></vitesse-endpoint-op-editor>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-endpoint-viewport': EndpointViewport;
  }
}
